import { useEffect, useMemo, useState } from 'react';
import { apiClient } from '@/api/client';
import { useScope, brandColorVar } from '@/context/ScopeContext';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { BrandAccountChip } from '@/components/BrandAccountChip';
import { VariantCard } from '@/components/VariantCard';
import { Sparkles, Send, CalendarClock, FilePen, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import TESTIDS from '@/constants/testIds';

const CONTENT_TYPES = [
    { value: 'feed_post', label: 'Feed post' },
    { value: 'carousel', label: 'Carousel' },
    { value: 'reel_caption', label: 'Reel caption' },
    { value: 'story_idea', label: 'Story idea' },
    { value: 'text_post', label: 'Text post' },
];

export default function Composer() {
    const { brands, accounts, refresh } = useScope();
    const [brandId, setBrandId] = useState(brands[0]?.id || '');
    const [destinationIds, setDestinationIds] = useState([]);
    const [platform, setPlatform] = useState('instagram');
    const [contentType, setContentType] = useState('feed_post');
    const [topic, setTopic] = useState('');
    const [goal, setGoal] = useState('');
    const [tone, setTone] = useState('');
    const [cta, setCta] = useState('');
    const [mention, setMention] = useState('');
    const [location, setLocation] = useState('');

    const [generating, setGenerating] = useState(false);
    const [output, setOutput] = useState(null);
    const [promptId, setPromptId] = useState(null);
    const [selectedLabel, setSelectedLabel] = useState(null);
    const [editedCaption, setEditedCaption] = useState('');
    const [editedHashtags, setEditedHashtags] = useState('');
    const [editedFirstComment, setEditedFirstComment] = useState('');
    const [editedImagePrompt, setEditedImagePrompt] = useState('');

    const [draft, setDraft] = useState(null);
    const [savingDraft, setSavingDraft] = useState(false);

    const [scheduleOpen, setScheduleOpen] = useState(false);
    const [scheduleAt, setScheduleAt] = useState('');
    const [publishConfirmOpen, setPublishConfirmOpen] = useState(false);

    useEffect(() => {
        if (!brandId && brands.length) setBrandId(brands[0].id);
    }, [brands, brandId]);

    const composerAccounts = useMemo(
        () => accounts.filter((a) => a.brand_id === brandId),
        [accounts, brandId],
    );

    useEffect(() => {
        setDestinationIds((prev) => prev.filter((id) => composerAccounts.some((a) => a.id === id)));
    }, [composerAccounts]);

    const selectedBrand = brands.find((b) => b.id === brandId);
    const accent = brandColorVar(selectedBrand?.color_slot);

    const selectedVariant = useMemo(() => {
        if (!output) return null;
        return (output.captions || []).find((c) => c.label === selectedLabel) || output.captions?.[0];
    }, [output, selectedLabel]);

    const applyVariant = (variant) => {
        setSelectedLabel(variant.label);
        setEditedCaption(variant.long || variant.short || '');
        setEditedHashtags((variant.hashtags || []).join(' '));
        setEditedFirstComment(variant.first_comment || '');
        setEditedImagePrompt(output?.image_prompt || '');
    };

    const generate = async () => {
        if (!brandId) {
            toast.error('Choose a brand');
            return;
        }
        if (!topic.trim()) {
            toast.error('Topic is required');
            return;
        }
        setGenerating(true);
        setOutput(null);
        setSelectedLabel(null);
        setDraft(null);
        try {
            const payload = {
                brand_id: brandId,
                destination_account_ids: destinationIds,
                platform,
                content_type: contentType,
                topic: topic.trim(),
                goal: goal.trim(),
                tone_override: tone.trim() || null,
                cta_hint: cta.trim() || null,
                mention: mention.trim() || null,
                location: location.trim() || null,
            };
            const r = await apiClient.post('/content/generate', payload);
            setOutput(r.data.output);
            setPromptId(r.data.prompt_id);
            if (r.data.output?.captions?.[0]) {
                applyVariant(r.data.output.captions[0]);
            }
            toast.success('Generated 3 variants');
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Generation failed');
        } finally {
            setGenerating(false);
        }
    };

    const saveDraft = async () => {
        if (!output) {
            toast.error('Generate content first');
            return;
        }
        if (destinationIds.length === 0) {
            toast.error('Pick at least one destination account');
            return;
        }
        setSavingDraft(true);
        try {
            const hashtags = editedHashtags
                .split(/\s+/)
                .map((t) => t.trim())
                .filter(Boolean);
            const r = await apiClient.post('/content/drafts', {
                brand_id: brandId,
                destination_account_ids: destinationIds,
                platform,
                content_type: contentType,
                request_payload: { brand_id: brandId, platform, content_type: contentType, topic, goal, tone_override: tone, cta_hint: cta, mention, location },
                output,
                selected_variant_label: selectedLabel,
                edited_caption: editedCaption,
                edited_hashtags: hashtags,
                edited_first_comment: editedFirstComment,
                edited_image_prompt: editedImagePrompt,
            });
            setDraft(r.data);
            toast.success('Draft saved');
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Failed to save draft');
        } finally {
            setSavingDraft(false);
        }
    };

    const schedule = async () => {
        if (!draft) {
            toast.error('Save the draft first');
            return;
        }
        if (!scheduleAt) {
            toast.error('Pick a date and time');
            return;
        }
        try {
            const localDate = new Date(scheduleAt);
            await apiClient.post('/content/schedule', {
                draft_id: draft.id,
                destination_account_ids: destinationIds,
                scheduled_at: localDate.toISOString(),
            });
            setScheduleOpen(false);
            toast.success(`Scheduled for ${localDate.toLocaleString()}`);
            await refresh();
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Schedule failed');
        }
    };

    const publishNow = async () => {
        if (!draft) {
            toast.error('Save the draft first');
            return;
        }
        try {
            const r = await apiClient.post('/content/publish', {
                draft_id: draft.id,
                destination_account_ids: destinationIds,
            });
            setPublishConfirmOpen(false);
            toast.success(`Sent to ${r.data.results.length} destinations (placeholder Meta)`);
            await refresh();
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Publish failed');
        }
    };

    return (
        <div className="space-y-4" style={{ '--brand-accent-current': accent }}>
            <div>
                <h1 className="text-2xl font-semibold tracking-tight">Composer</h1>
                <p className="text-sm text-muted-foreground">
                    Type a prompt. Pick destinations. Generate 3 variants in the right brand voice.
                </p>
            </div>

            <div className="grid grid-cols-1 gap-4 xl:grid-cols-[420px_1fr]">
                <Card className="flex flex-col gap-4 border-border bg-card p-4">
                    <div className="space-y-3">
                        <div>
                            <Label>Brand</Label>
                            <Select value={brandId} onValueChange={(v) => { setBrandId(v); setDestinationIds([]); }}>
                                <SelectTrigger data-testid={TESTIDS.composerBrandSelect}>
                                    <SelectValue placeholder="Pick a brand" />
                                </SelectTrigger>
                                <SelectContent>
                                    {brands.map((b) => (
                                        <SelectItem key={b.id} value={b.id}>
                                            <span
                                                className="mr-2 inline-block h-2 w-2 rounded-full"
                                                style={{ backgroundColor: `hsl(${brandColorVar(b.color_slot)})` }}
                                            />
                                            {b.name}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label>Destination accounts</Label>
                            <p className="text-[11px] text-muted-foreground">Tap to toggle one or more accounts. Confirmation required before multi-account publish.</p>
                            <div className="mt-2 flex flex-wrap gap-1.5">
                                {composerAccounts.length === 0 ? (
                                    <div className="text-xs text-muted-foreground">No accounts for this brand yet.</div>
                                ) : null}
                                {composerAccounts.map((a) => {
                                    const selected = destinationIds.includes(a.id);
                                    return (
                                        <BrandAccountChip
                                            key={a.id}
                                            account={a}
                                            brand={selectedBrand}
                                            selected={selected}
                                            onClick={() =>
                                                setDestinationIds((prev) =>
                                                    selected
                                                        ? prev.filter((id) => id !== a.id)
                                                        : [...prev, a.id],
                                                )
                                            }
                                            testId={TESTIDS.composerAccountChip}
                                        />
                                    );
                                })}
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                            <div>
                                <Label>Platform</Label>
                                <Select value={platform} onValueChange={setPlatform}>
                                    <SelectTrigger data-testid={TESTIDS.composerPlatformSelect}>
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="instagram">Instagram</SelectItem>
                                        <SelectItem value="facebook" disabled>Facebook (coming)</SelectItem>
                                        <SelectItem value="x" disabled>X (coming)</SelectItem>
                                        <SelectItem value="linkedin" disabled>LinkedIn (coming)</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <Label>Content type</Label>
                                <Select value={contentType} onValueChange={setContentType}>
                                    <SelectTrigger data-testid={TESTIDS.composerContentTypeSelect}>
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {CONTENT_TYPES.map((c) => (
                                            <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                        <div>
                            <Label>Topic</Label>
                            <Textarea rows={2} value={topic} onChange={(e) => setTopic(e.target.value)} placeholder="e.g. Sunflower microgreens grown locally in Idaho" data-testid={TESTIDS.composerTopicInput} />
                        </div>
                        <div>
                            <Label>Goal</Label>
                            <Input value={goal} onChange={(e) => setGoal(e.target.value)} placeholder="e.g. educate and convert to weekly subscription" data-testid={TESTIDS.composerGoalInput} />
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                            <div>
                                <Label>Tone override</Label>
                                <Input value={tone} onChange={(e) => setTone(e.target.value)} placeholder="optional" data-testid={TESTIDS.composerToneInput} />
                            </div>
                            <div>
                                <Label>CTA hint</Label>
                                <Input value={cta} onChange={(e) => setCta(e.target.value)} placeholder="optional" data-testid={TESTIDS.composerCtaInput} />
                            </div>
                            <div>
                                <Label>Mention</Label>
                                <Input value={mention} onChange={(e) => setMention(e.target.value)} placeholder="product / service" data-testid={TESTIDS.composerMentionInput} />
                            </div>
                            <div>
                                <Label>Location</Label>
                                <Input value={location} onChange={(e) => setLocation(e.target.value)} placeholder="optional" data-testid={TESTIDS.composerLocationInput} />
                            </div>
                        </div>
                    </div>
                    <Button onClick={generate} disabled={generating} className="w-full" data-testid={TESTIDS.composerGenerateButton}>
                        <Sparkles className="mr-2 h-4 w-4" /> {generating ? 'Generating…' : 'Generate 3 variants'}
                    </Button>
                </Card>

                <div className="space-y-4">
                    {!output ? (
                        <Card className="flex h-64 flex-col items-center justify-center gap-2 border-dashed border-border bg-card text-center text-sm text-muted-foreground" data-testid="empty-state-composer-generate">
                            <Sparkles className="h-6 w-6" />
                            Generate options. Choose a brand and destinations, then click Generate to produce 3 caption variants to compare side-by-side.
                        </Card>
                    ) : (
                        <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
                            {(output.captions || []).map((v) => (
                                <VariantCard key={v.label} variant={v} selected={selectedLabel === v.label} onUse={applyVariant} />
                            ))}
                        </div>
                    )}

                    {output ? (
                        <Card className="border-border bg-card p-4">
                            <div className="mb-2 flex items-center justify-between">
                                <h3 className="text-base font-semibold">Edit & approve</h3>
                                <div className="font-mono text-[11px] text-muted-foreground">{editedCaption.length} chars</div>
                            </div>
                            <div className="space-y-3">
                                <div>
                                    <Label>Caption</Label>
                                    <Textarea rows={5} value={editedCaption} onChange={(e) => setEditedCaption(e.target.value)} data-testid={TESTIDS.composerEditCaption} />
                                </div>
                                <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                                    <div>
                                        <Label>Hashtags</Label>
                                        <Textarea rows={2} value={editedHashtags} onChange={(e) => setEditedHashtags(e.target.value)} />
                                    </div>
                                    <div>
                                        <Label>First comment</Label>
                                        <Textarea rows={2} value={editedFirstComment} onChange={(e) => setEditedFirstComment(e.target.value)} />
                                    </div>
                                </div>
                                <div>
                                    <Label>Image prompt</Label>
                                    <Textarea rows={2} value={editedImagePrompt} onChange={(e) => setEditedImagePrompt(e.target.value)} />
                                </div>
                                {output.reel_hook ? (
                                    <div className="rounded-md border border-dashed border-border bg-[hsl(var(--surface-2))] p-3">
                                        <div className="text-[11px] uppercase tracking-wider text-muted-foreground">Reel hook</div>
                                        <div className="text-sm">{output.reel_hook}</div>
                                        {output.reel_script ? (
                                            <div className="mt-2 whitespace-pre-wrap text-xs text-muted-foreground">{output.reel_script}</div>
                                        ) : null}
                                    </div>
                                ) : null}
                                <div className="flex flex-wrap items-center gap-2">
                                    <Button onClick={saveDraft} disabled={savingDraft} data-testid={TESTIDS.composerSaveDraft}>
                                        <FilePen className="mr-1 h-4 w-4" /> {savingDraft ? 'Saving…' : 'Save draft'}
                                    </Button>
                                    <Button
                                        variant="outline"
                                        disabled={!draft}
                                        onClick={() => setScheduleOpen(true)}
                                        data-testid={TESTIDS.composerSchedule}
                                    >
                                        <CalendarClock className="mr-1 h-4 w-4" /> Schedule
                                    </Button>
                                    <Button
                                        variant="outline"
                                        disabled={!draft || destinationIds.length === 0}
                                        onClick={() => setPublishConfirmOpen(true)}
                                        data-testid={TESTIDS.composerPublishNow}
                                    >
                                        <Send className="mr-1 h-4 w-4" /> Publish now
                                    </Button>
                                    {draft ? (
                                        <span className="ml-auto text-[11px] text-muted-foreground">Draft id <span className="font-mono">{draft.id.slice(0, 8)}…</span></span>
                                    ) : null}
                                </div>
                            </div>
                        </Card>
                    ) : null}
                </div>
            </div>

            <AlertDialog open={scheduleOpen} onOpenChange={setScheduleOpen}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Schedule this draft</AlertDialogTitle>
                        <AlertDialogDescription>
                            Will send to {destinationIds.length} account{destinationIds.length === 1 ? '' : 's'} at the time below.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <div className="space-y-2">
                        <Label htmlFor="sched-at">Send at</Label>
                        <Input
                            id="sched-at"
                            type="datetime-local"
                            value={scheduleAt}
                            onChange={(e) => setScheduleAt(e.target.value)}
                            data-testid={TESTIDS.composerScheduleDateInput}
                        />
                    </div>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={(e) => { e.preventDefault(); schedule(); }} data-testid={TESTIDS.composerScheduleConfirm}>
                            Schedule
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            <AlertDialog open={publishConfirmOpen} onOpenChange={setPublishConfirmOpen}>
                <AlertDialogContent data-testid={TESTIDS.composerPublishConfirmDialog}>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Publish to {destinationIds.length} account{destinationIds.length === 1 ? '' : 's'}?</AlertDialogTitle>
                        <AlertDialogDescription>
                            Real Meta credentials are placeholders, so each destination will land in <span className="font-mono">pending_external</span> until real OAuth is connected. Audit log + publish log are still written.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <div className="flex flex-wrap gap-1.5">
                        {accounts
                            .filter((a) => destinationIds.includes(a.id))
                            .map((a) => (
                                <BrandAccountChip key={a.id} account={a} brand={selectedBrand} selected />
                            ))}
                    </div>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={(e) => { e.preventDefault(); publishNow(); }} data-testid={TESTIDS.composerPublishConfirm}>
                            Confirm publish
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}
